<template>
  <div>
    <div class="position-grid-field">
      <div class="flex justify-content-between flex-wrap">
        <div v-if="configs.label !== ''" class="flex justify-content-center">
          <label class="block my-3">
            {{ t(configs.label, null) }}
          </label>
        </div>
      </div>
      <div class="position-grid-container">
        <div class="position-grid">
          <div class="position-grid__center" />
          <div class="position-grid__preview">
            <template v-for="position in positions" :key="position.value">
              <div
                v-if="position.value !== 'mc' || allowCenter"
                class="position-grid__dot"
                :class="{
                  'position-grid__dot--active':
                    currentPosition === position.value,
                  'position-grid__dot--hover':
                    hoveredPosition === position.value
                }"
                @click="selectPosition(position.value)"
                @mouseenter="hoveredPosition = position.value"
                @mouseleave="hoveredPosition = null"
              />
              <div v-else class="position-grid__placeholder" />
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch, onMounted } from "vue";
import { ParserFieldObject, Property } from "@/types";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "PositionGrid",

  props: {
    configs: {
      type: Object as PropType<any>,
      required: true
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true
    }
  },

  emits: ['on-change-properties'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const positions = [
      { value: "tl", row: 1, col: 1 },
      { value: "tc", row: 1, col: 2 },
      { value: "tr", row: 1, col: 3 },
      { value: "ml", row: 2, col: 1 },
      { value: "mc", row: 2, col: 2 },
      { value: "mr", row: 2, col: 3 },
      { value: "bl", row: 3, col: 1 },
      { value: "bc", row: 3, col: 2 },
      { value: "br", row: 3, col: 3 }
    ];

    const hoveredPosition = ref<string | null>(null);

    // Allow center position (mc) - disabled by default
    const allowCenter = props.configs.options?.allowCenter || false;

    const currentPosition = ref(props.configs.options?.defaultValue || "bl");

    // Initialize from parsed values
    const initPosition = () => {
      const property = props.parserValues.properties?.[0];
      if (property?.value) {
        currentPosition.value = property.value;
      }
    };

    onMounted(() => {
      initPosition();
    });

    watch(
      () => props.parserValues.properties,
      () => {
        initPosition();
      },
      { deep: true }
    );

    const selectPosition = (position: string) => {
      currentPosition.value = position;
      const property = props.configs.properties?.[0];
      if (property) {
        const prop: Property = {
          name: property.name,
          getter: property.getter,
          setters: property.setters,
          value: position
        };
        emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [prop],
        });
      }
    };

    return {
      t,
      positions,
      currentPosition,
      hoveredPosition,
      allowCenter,
      selectPosition
    };
  }
});
</script>

<style lang="scss" scoped>
.position-grid-container {
  display: flex;
  justify-content: center;
  padding: 0.25rem 0;
}

.position-grid {
  width: 100px;
  height: 100px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  position: relative;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.position-grid__center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 28px;
  height: 28px;
  background: var(--surface-100, #f3f4f6);
  border-radius: 6px;
  border: 1px dashed var(--surface-400, #9ca3af);
  pointer-events: none;
  z-index: 0;
}

.position-grid__preview {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
  padding: 4px;
  position: relative;
  z-index: 1;
}

.position-grid__placeholder {
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.position-grid__dot {
  width: 6px;
  height: 6px;
  justify-self: center;
  align-self: center;
  border-radius: 50%;
  background: var(--surface-300, #d1d5db);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;

  /* Larger click area using pseudo-element */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }

  /* Hover state for unselected dots */
  &:not(.position-grid__dot--active):hover {
    background: var(--surface-500, #6b7280);
    transform: scale(1.4);
  }

  /* Active state transformations */
  &--active {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: var(--primary-color, #3b82f6);
    box-shadow: 0 4px 6px -1px rgba(var(--primary-color-rgb, 59, 130, 246), 0.3);

    &::before {
      display: none;
    }
  }
}
</style>
